"use client";

import { useEffect, useRef } from "react";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { useSearchParams } from "next/navigation";
import { useToast } from "@/contexts/toast-context";
import { PricingSection } from "@/components/ui/pricing-section";
import { isSubscriptionOnHold } from "@/lib/billing/entitlements";
import { diamondMonthlyGap } from "@/lib/billing/diamond-gap";
import { featureGateRoute, normalizeProductTypeParam } from "@/lib/billing/feature-gate";
import { BUNDLE_TIER_NAME, memberHoldsPlan, planSubscriptionFromRow } from "@/lib/billing/purchase-route";
import { NAV_SIGNED_OUT_VARIANT_CLASS } from "@/lib/nav-auth-hint";
import { GOLD_MONTHLY_PRICE_ID } from "@/lib/billing/tier-price-ids";
import { usePublicAuth } from "@/lib/public-auth";
import { defaultTiers, comparisonTools } from "../../../components/pricing-tiers";
import { phCapture } from "@/utils/analytics";
import { cn } from "@/lib/utils";
import { ProductType } from "./types";

const KNOWN_PRODUCT_TYPES = new Set<string>(Object.values(ProductType));

export function PricingSubscriptionRequiredToast() {
  const searchParams = useSearchParams();
  const { showToast } = useToast();
  const { isLoading, subscriptions } = usePublicAuth();
  const toastShown = useRef(false);

  useEffect(() => {
    if (!searchParams || toastShown.current || isLoading) return;
    if (searchParams.get("redirect") !== "subscription_required") return;
    const rawProductType = searchParams.get("product_type");
    const productType =
      rawProductType && KNOWN_PRODUCT_TYPES.has(rawProductType)
        ? rawProductType
        : "unknown";
    const source =
      searchParams.get("redirect_to")?.split(/[?#]/, 1)[0] || "unknown";
    const pageName = productType === "unknown" ? "this tool" : productType;
    const gatedProduct = normalizeProductTypeParam(rawProductType);
    const onBreak = subscriptions.some((sub) => isSubscriptionOnHold(sub));
    const route = gatedProduct && !onBreak
      ? featureGateRoute(subscriptions.map(planSubscriptionFromRow), gatedProduct)
      : null;
    const upgradeTier = route?.kind === "plan_change" ? route.targetTier : null;
    const fromGoldMonthly =
      route?.kind === "plan_change" &&
      route.targetTier === "diamond" &&
      route.targetInterval === "month" &&
      subscriptions.some((sub) => sub.price_id === GOLD_MONTHLY_PRICE_ID && (sub.status === "active" || sub.status === "trialing"));
    const goldGap = fromGoldMonthly ? diamondMonthlyGap("gold") : null;
    phCapture("pricing_subscription_required_prompted", {
      surface: "pricing_page",
      product_type: productType,
      source,
      ...(upgradeTier ? { upgrade_tier: upgradeTier } : {}),
    });
    if (upgradeTier) {
      showToast(
        goldGap
          ? `${pageName} comes with Diamond, which is ${goldGap} a month more than Gold. You'll see exactly what's due today before you confirm the upgrade.`
          : `${pageName} comes with ${BUNDLE_TIER_NAME[upgradeTier]}. You'll see exactly what's due today before you confirm the upgrade.`,
        "info",
        `Upgrade to open ${pageName}`,
      );
    } else {
      showToast(
        `You need an active subscription to access ${pageName}. Please choose a plan below.`,
        "warning",
        "Subscription Required",
      );
    }
    toastShown.current = true;
  }, [isLoading, searchParams, showToast, subscriptions]);

  return null;
}

function useMemberTrialCopy() {
  const { isLoading, subscriptions } = usePublicAuth();
  return { isLoading, hidden: !isLoading && memberHoldsPlan(subscriptions.map(planSubscriptionFromRow)) };
}

export function PricingHeroTrialSentence() {
  const { isLoading, hidden } = useMemberTrialCopy();
  if (hidden) return null;
  return <span className={isLoading ? NAV_SIGNED_OUT_VARIANT_CLASS : undefined}> Try free for 5 days.</span>;
}

export function PricingHeroCta() {
  const { isLoading, hidden } = useMemberTrialCopy();
  if (hidden) return null;
  return (
    <div className={cn("mt-6", isLoading && "[.nav-auth_&]:invisible")}>
      <button type="button"
        onClick={() => {
          const section = document.getElementById("pricing");
          if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        className="inline-flex min-h-11 w-full items-center justify-center gap-2 px-8 py-3 bg-accent-value hover:bg-accent-value-strong text-zinc-950 font-semibold rounded-xl transition-all duration-200 sm:w-auto"
      >
        Try Free for 5 Days
        <ArrowRightIcon className="size-4" />
      </button>
    </div>
  );
}

export function PricingPlans() {
  const viewed = useRef(false);
  const { isLoading, hidden } = useMemberTrialCopy();

  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    phCapture("pricing_page_viewed", { surface: "pricing_page" });
  }, []);

  return (
    <>
      <PricingSection tiers={defaultTiers} comparison={comparisonTools} />
      {!hidden && (
        <div className={cn("text-center mt-16", isLoading && NAV_SIGNED_OUT_VARIANT_CLASS)}>
          <p className="text-muted-foreground text-sm">
            5-day free trial on monthly plans · Cancel anytime · New subscribers only
          </p>
        </div>
      )}
    </>
  );
}
